import React, {useState, useEffect, useCallback} from 'react'
/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react'

import {connect} from 'react-redux'

import useMediaQuery from '@mui/material/useMediaQuery'

import {startOfToday} from 'date-fns'

import {get} from 'lodash'

import {dateToUTC} from '../../../utils'

import {useIsMounted} from '../../../hooks'

import { 
  makeGetBandById,
  selectBandLinks,
  makeGetBandPerformances,
  makeGetNearbyBandPerformances,
  selectBandFetchedAssociations
} from '../../../selectors'


import { 
  index,
  saveLinks, 
  showAlert,
  addPerformancesToBand,
  setFetchedAssociations,
  addNearbyPerformancesToBand,
} from '../../../actions'


import Dialog from '@mui/material/Dialog'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import HistoryIcon from '@mui/icons-material/History'

import {PerformanceItemContainer} from '../../cards/CardList'
import {LeavePerformance} from '../../performance'
import EventForm from '../../performance/form/EventForm'
import {CardList, CardListMobile, PerformanceCard} from '../../cards'

import {  
  GroupedButton,
  FadeTransition,
  SlideTransition,
  ThreeDotProgress,
  DetailPageTabMessage,
  DetailPageTabButtonContainer
} from '../../generics'




const styles = {
  root: css`
    height: 100%;
    width: 100%;
    display: flex;
    align-items: center;
    align-content: center;
    justify-content: flex-start;
    flex-direction: column;
  `,
  section: css`
    width: 100%;
    display: flex;
    flex-direction: column;
    margin-bottom: 60px;

    h4 {
      font-size: 1rem;
      font-weight: 600;
      color: #7b7b7b;
      letter-spacing: 0;
      margin-left: 15px;
      margin-right: 15px;
      margin-bottom: 15px;
      padding-bottom: 5px;
      border-bottom: 1px solid #d9d9d9;
      margin-top: 10px;
      text-transform: uppercase;
    }
  `,
  cardsContainer: css`
    display: flex;
    flex-wrap: wrap;
    max-width: 100%;
    width: 100%;

    .message {
      width: 100%;
      font-size: 1rem;
      text-align: left;
      padding-left: 15px;
      margin-bottom: 15px;
    }
  `,
  progressContainer: css`
    height: 100%;
    width: 100%;
    margin-top: 20px;
  `,
  dialog: css`
    backdrop-filter: blur(10px);
  `
}




const BandPerformances = props => {

  const isMounted = useIsMounted()

  const isExtraSmallScreen = useMediaQuery(theme => theme.breakpoints.only('xs'))
  const isLessThanMediumScreen = useMediaQuery(theme => theme.breakpoints.down('md'))
  const isLessThanLargeScreen = useMediaQuery(theme => theme.breakpoints.down('lg'))
  
  const [modal, setModal] = useState(false)
  


  const { 
    bandId, 
    performances,
    nearbyPerformances,
    index,
    permission,
    addPerformancesToBand,
    addNearbyPerformancesToBand,
    setFetchedAssociations,
    isFetching,
    isLoaded,
    lat,
    lng,
    saveLinks,
    stateLinks
  } = props



  const [links, setLinks] = useState(stateLinks)

  const [isFetched, setIsFetched] = useState(isLoaded)

  const hasNextPage = (links.pages !== links.page)





  const fetchMorePerformances = useCallback(props => {
    if (hasNextPage) {
      const url = links.next_url.slice(4)

      index(url, 'PERFORMANCES').then(response => {
        addPerformancesToBand(bandId, response.data)

        if (isMounted.current) {
          if (props.isMounted && props.isMounted.current && props.setIsFetching) {
            props.setIsFetching(false)
          }

          if (response.links) {
            setLinks(response.links)
          }
          
          setIsFetched(true)  
        }
      })
    }
  }, [
    addPerformancesToBand,
    bandId,
    index,
    isMounted,
    links.next_url,
    hasNextPage
  ])



  useEffect(() => {
    return () => saveLinks('BAND', bandId, 'performances', links)
  }, [bandId, links, saveLinks])




  useEffect(() => {
    if (!isLoaded) {

      const url = '/bands/' + bandId + '/performances'

      const time = dateToUTC(startOfToday())

      const data = {time, lat, lng}
      
      setFetchedAssociations('BAND', bandId, 'performances')

      index(url, 'PERFORMANCES', data).then(response => {
        const nearby = get(response, ['meta', 'nearby', 'data'], [])

        addPerformancesToBand(bandId, response.data)

        if (isMounted.current) {
          setIsFetched(true)

          if (response.links) {
            setLinks(response.links)
          }
          
          if (nearby && nearby.length) {
            addNearbyPerformancesToBand(bandId, nearby)
          }
        }
      })
    }
  }, [
    lat,
    lng,
    index,
    bandId,
    isLoaded,
    isMounted,
    addPerformancesToBand,
    setFetchedAssociations,
    addNearbyPerformancesToBand
  ])



  function handleHistoryClick() {

    const url = '/bands/' + bandId + '/performances'
    
    const data = {show_past: true, time: dateToUTC(startOfToday()), lat, lng}


    index(url, 'PERFORMANCES', data).then(response => {
      addPerformancesToBand(bandId, response.data, true)

      if (isMounted.current && response.links) {
        setLinks(response.links)
      }
    })
  }





  const open = content => () => {
    setModal(content)
  }



  function close() {
    setModal('')
  }





  const NearbyContent = useCallback(() => {
    if (!nearbyPerformances.length) return null


    return nearbyPerformances.map(performance => {
      if (isExtraSmallScreen) {
        return <PerformanceCard key={performance.id} performance={performance} />
      } else {
        return (
          <PerformanceItemContainer key={performance.id}>
            <PerformanceCard performance={performance} />
          </PerformanceItemContainer>
        )
      }
    })
  }, [nearbyPerformances, isExtraSmallScreen])






  return (
    <div css={styles.root}>
      <DetailPageTabButtonContainer>
        <>
          {['owner', 'editor'].includes(permission) &&
            <>
              <GroupedButton 
                text='New'
                tip='Create new performance'
                icon={<AddIcon />}
                onClick={open('create')}  
              /> 

              <GroupedButton 
                text='leave'
                tip='Leave an exisiting performance'
                icon={<RemoveIcon />}
                onClick={open('leave')} 
              />
            </>
          }
          
{/*          <GroupedButton
            text='History'
            tip='Show previous performances'
            icon={<HistoryIcon />}
            onClick={handleHistoryClick}
          />*/}
        </>
      </DetailPageTabButtonContainer>



      {Boolean(nearbyPerformances.length) &&
        <section css={styles.section}>
          <h4>NEAR YOU</h4> 

          <div css={styles.cardsContainer}>
            <NearbyContent />
          </div>
        </section>
      }


      <section css={styles.section}>
        {Boolean(performances.length) && 
          <>
            <h4>ALL</h4>


            {isExtraSmallScreen &&
              <CardListMobile
                type='performance'
                cards={performances}
                hasNextPage={hasNextPage}
                fetchMoreItems={fetchMorePerformances}
              />
            }


            {!isExtraSmallScreen &&
              <CardList 
                type='performance'
                cards={performances}
                hasNextPage={hasNextPage}
                fetchMoreItems={fetchMorePerformances}
              /> 
            }
          </>
        }



        {!Boolean(performances.length) && isFetched &&
          <DetailPageTabMessage>
            {['owner', 'editor'].includes(permission)
              ? 'This band hasn\'t published any upcoming performances yet.'
              : 'To create a performance, click the plus icon above.'
            }
          </DetailPageTabMessage>
        }



        {isFetching && 
          <div css={styles.progressContainer}>
            <ThreeDotProgress position='sticky' /> 
          </div>
        }
      </section>





      {['owner', 'editor', 'member'].includes(permission) && 
        <>
          <Dialog
            open={modal === 'create'}
            scroll='body'
            sx={{
              '& .MuiDialog-container': {
                '& .MuiPaper-root': {
                  maxWidth: '1200px'
                }
              }
            }}
            css={styles.dialog}
            onClose={close}
            fullWidth={!isLessThanLargeScreen}
            fullScreen={isLessThanMediumScreen}
            TransitionComponent={isLessThanMediumScreen ? SlideTransition : FadeTransition}
          >
            <EventForm creatorId={bandId} creatorType='band' close={close} />
          </Dialog>


          <Dialog
            css={styles.dialog}
            open={modal === 'leave'}
            onClose={close}
            maxWidth='sm'
            fullWidth={true}
            fullScreen={isLessThanLargeScreen}
            TransitionComponent={isLessThanLargeScreen ? SlideTransition : FadeTransition }
          >
            <LeavePerformance 
              performances={performances} 
              handleClose={close}
              performer={{id: bandId, type: 'Band'}} 
            />
          </Dialog>
        </>
      }
    </div>
  )
}


const makeMapStateToProps = () => {
  const getBandById = makeGetBandById()
  const getBandPerformances = makeGetBandPerformances()
  const getNearbyBandPerformances = makeGetNearbyBandPerformances()
  

  const mapStateToProps = (state, props) => {
    
    const id = props.bandId

    const band = getBandById(state, id).attributes
    
    const links = selectBandLinks(state, id, 'performances')

    const performances = getBandPerformances(state, id)
    
    const nearbyPerformances = getNearbyBandPerformances(state, id)

    const role = get(state, ['user', 'attributes', 'role'])


    return {
      lng: state.user.location.lng,
      lat: state.user.location.lat,
      stateLinks: links,
      permission: ['admin', 'editor'].includes(role) ? 'owner' : band?.permission,
      performances: performances,
      nearbyPerformances: nearbyPerformances,
      isFetching: state.performances.isFetching && performances.length === 0,
      isLoaded: selectBandFetchedAssociations(state, id).includes('performances')
    }
  }

  return mapStateToProps
}




const actions = { 
  index,
  saveLinks, 
  showAlert,
  addPerformancesToBand, 
  addNearbyPerformancesToBand, 
  setFetchedAssociations
}


export default connect(makeMapStateToProps, actions)(BandPerformances)


